import {
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Autocomplete,
  CircularProgress,
} from '@achieve/ascend'
import { Typography } from 'components/Contentful'
import { Button } from '@achieve/ascend'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { useHeloc } from './HelocContext'

const HelocCalculatorHomeValue = ({ onSetHomeValue }) => {
  const { state, dispatch } = useHeloc()
  const [estimateAddressData, setEstimateAddressData] = useState(null)
  const [addressDetails, setAddressDetails] = useState(null)
  const [homeValue, setHomeValue] = useState(null)
  const [addressList, setAddressList] = useState([])
  const [session] = useState(uuidv4())
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)
  const { fireAndForget } = useTriggerEvents()

  const searchAddress = async (address) => {
    setIsLoadingAddress(true)
    try {
      const list = []
      if (address) {
        const autocomplete = await fetch(`/api/places/autocomplete`, {
          method: 'POST',
          body: JSON.stringify({ address, session }),
        })
        const data = await autocomplete.json()
        setAddressList(data.map((item) => ({ id: item.place_id, label: item.description })))
      } else {
        setAddressList(list)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAddress(false)
    }
  }

  const handleAddressSelect = (value) => {
    setAddressDetails(value)
    // close keyboard on mobile
    if (document.activeElement) {
      document.activeElement.blur()
    }
  }

  const getHomeValue = async (address) => {
    try {
      setIsLoading(true)
      const homeValue = await fetch(`/api/places/home-value`, {
        method: 'POST',
        body: JSON.stringify({ place_id: address.id, session }),
      })
      const data = await homeValue.json()
      setHomeValue({
        addressValue: data.homeValue,
        addressState: data.address.state,
      })
      fireAndForget({ event_type: 'heloc_calc_house_canary_input', address, found: true })
    } catch (error) {
      console.error(error)
      fireAndForget({ event_type: 'heloc_calc_house_canary_input', address, found: false })
      setHomeValue({
        addressValue: null,
        addressState: null,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const list = []
    setHomeValue(null)
    if (estimateAddressData) {
      searchAddress(estimateAddressData)
    }
    setAddressList(list)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateAddressData])

  useEffect(() => {
    if (addressDetails) {
      getHomeValue(addressDetails)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressDetails])

  return (
    <Dialog
      open={state.modalEstimate}
      onClose={() => {
        dispatch({ type: 'SET_MODAL_ESTIMATE', payload: false })
      }}
      aria-labelledby="alert-dialog-estimate-title"
      aria-describedby="alert-dialog-estimate-description"
      PaperProps={{
        className:
          'sm:max-w-md sm:rounded-2xl sm:mx-auto sm:my-16 w-full max-w-full fixed lg:relative bottom-0 left-0 right-0 rounded-none rounded-t-3xl m-0 min-h-[40vh] sm:min-h-0',
        style: { boxShadow: '0 8px 32px rgba(0,0,0,0.16)' },
      }}
    >
      <DialogContent>
        <DialogTitle id="alert-dialog-title" className="flex justify-end p-0 pt-4 pb-2 relative">
          <div className="absolute left-0 right-0 flex justify-center pointer-events-none">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mb-2"></div>
          </div>
        </DialogTitle>
        <div className="flex flex-col gap-6 px-4 py-6">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <Typography content={'Enter your current address'} className="bodyMd font-medium" />
            </div>
            <Autocomplete
              data-testid="estimateAddress"
              name="estimateAddress"
              options={addressList}
              value={estimateAddressData}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_e, value) => {
                setEstimateAddressData(value)
              }}
              onChange={(_e, value) => {
                handleAddressSelect(value)
              }}
              loading={isLoadingAddress}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="123 Main"
                  sx={{
                    '& .MuiAutocomplete-endAdornment': {
                      top: '50%',
                    },
                  }}
                />
              )}
            />
          </div>
          {homeValue && (
            <div className="flex flex-col gap-6">
              <div
                className={classNames(
                  'flex flex-col gap-2 p-4 rounded-md',
                  homeValue.addressValue ? 'bg-blue-50' : 'bg-yellow-50'
                )}
              >
                <Typography
                  content={
                    homeValue.addressValue
                      ? 'The estimate on your home is'
                      : "We're sorry, we couldn't find your home value. Please enter your estimate."
                  }
                  className="bodyMd font-regular"
                />
                {homeValue.addressValue && (
                  <Typography
                    content={`$${`${Number(homeValue.addressValue).toLocaleString('en-US')}`}`}
                    className="bodyMd font-bold!"
                  />
                )}
              </div>
              <Typography
                content={'The data is provided via HouseCanary'}
                className="bodySm text-gray-500 text-center"
              />
            </div>
          )}
          {isLoading && (
            <div className="flex justify-center items-center">
              <CircularProgress className="w-6 h-6 ms-2" size={30} />
            </div>
          )}
          <Button
            type="button"
            onClick={() => {
              if (homeValue?.addressValue) {
                dispatch({ type: 'SET_MODAL_ESTIMATE', payload: false })
                onSetHomeValue(homeValue)
              } else {
                dispatch({ type: 'SET_MODAL_ESTIMATE', payload: false })
              }
            }}
            id="save-home-value"
            color="primary"
            variant="contained"
            className="w-full"
          >
            {homeValue?.addressValue ? 'Save' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { HelocCalculatorHomeValue }
export default HelocCalculatorHomeValue
